/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useMemo, useRef } from "react";
import "./Dashboard.scss";

import cilpron_logo from "../../../Statics/Images/CPN.svg";
import { Button, Persona } from "@fluentui/react-components";
import {
  AlertBadgeRegular,
  AppsRegular,
  ChatHelpRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  Diversity24Regular,
  Home24Regular,
  LearningAppFilled,
  Payment24Regular,
  SearchRegular,
} from "@fluentui/react-icons";
import { makeStyles, shorthands, Tab, TabList } from "@fluentui/react-components";
import HMS from "../../../Statics/Images/HMSLOGOBG.png";
import BillingInvoices from "./Invoice/Invoice";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import { logoutThunk } from "../../../store/auth/authThunks";
import { selectUser } from "../../../store/auth/authSelectors";
import { CilpronApi } from "../../../constant/Banner";
import MegaMenu from "./Menu/MegaMenu";
import AppSwitcher from "./Apps/AppSwitcher";
import UserProfile from "./users/UserProfile";
import PricingPage from "./Pricing/PricingPage";
import SubscriptionFlow from "./SubscriptionFlow/SubscriptionFlow";
import type { Product, PlanFeature, Price, Plan, BillingCycle } from "../../../types/billingTypes";

const { BASE_URL, TOKEN_KEY } = CilpronApi;

// ── Product metadata ───────────────────────────────────────────
const PRODUCT_META: Record<string, { icon: string; color: string; bg: string }> = {
  "Hospitality Software":       { icon: "🏨", color: "#92400E", bg: "#FEF3C7" },
  "CRM Software":               { icon: "🤝", color: "#1E40AF", bg: "#DBEAFE" },
  "Email Services":             { icon: "✉️",  color: "#065F46", bg: "#D1FAE5" },
  "Swift Grid":                 { icon: "⚡",  color: "#5B21B6", bg: "#EDE9FE" },
  "Accounting":                 { icon: "📊",  color: "#0E7490", bg: "#CFFAFE" },
  "Chat Channel":               { icon: "💬",  color: "#9D174D", bg: "#FCE7F3" },
  "Call App":                   { icon: "📞",  color: "#1E3A5F", bg: "#DBEAFE" },
  "Agentic Customer Services":  { icon: "🤖",  color: "#78350F", bg: "#FEF3C7" },
  "API Access (Pay-As-You-Go)": { icon: "🔌",  color: "#1E40AF", bg: "#EFF6FF" },
  "Main SaaS Platform":         { icon: "🏢",  color: "#1E293B", bg: "#F1F5F9" },
  "White Label Add-on":         { icon: "🎨",  color: "#4C1D95", bg: "#F3E8FF" },
};
const DEFAULT_META = { icon: "📦", color: "#292929", bg: "#F5F5F5" };

// ── Price label ────────────────────────────────────────────────
function getPriceLabel(
  plan: Plan,
  price: Price | undefined,
  cycle: BillingCycle
): { main: string; sub: string } {
  if (plan.billingInterval === "lifetime") {
    if (!price || price.amount === 0) return { main: "One-time", sub: "contact sales" };
    return { main: `$${(price.amount / 100).toFixed(0)}`, sub: "one-time" };
  }
  if (!price) return { main: "Custom", sub: "contact sales" };
  if (price.pricing_model === "metered") {
    const u = (price.unit_amount ?? 0) / 100;
    if (u === 0) return { main: "Free", sub: `up to ${price.free_units.toLocaleString()} ${price.unit_label ?? "units"}` };
    return { main: `$${u < 0.01 ? u.toFixed(4) : u.toFixed(2)}`, sub: `per ${price.unit_label ?? "unit"}` };
  }
  if (price.pricing_model === "per_seat") {
    const dollars = (price.unit_amount ?? 0) / 100;
    if (dollars === 0) return { main: "Free", sub: "per seat / mo" };
    return { main: `$${dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2)}`, sub: "per seat / mo" };
  }
  if (price.amount === 0) return { main: "Free", sub: "forever" };
  const isYearly = cycle === "yearly" && plan.billingInterval === "yearly";
  const monthly  = isYearly ? price.amount / 12 : price.amount;
  const dollars  = monthly / 100;
  return {
    main: `$${dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2)}`,
    sub:  isYearly ? "/mo, billed yearly" : "/month",
  };
}

function isPlanFree(price: Price | undefined): boolean {
  if (!price) return false;
  return price.pricing_model === "flat" && price.amount === 0;
}

const INTERVAL_LABELS: Record<BillingCycle, string> = {
  monthly:  "Monthly",
  yearly:   "Yearly",
  lifetime: "Lifetime",
};

const useStyles = makeStyles({
  root: {
    alignItems: "flex-start",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    ...shorthands.padding("50px", "20px"),
    rowGap: "20px",
  },
});

const Dashboard: React.FC = () => {
  const [isActive, setIsActive]         = useState(false);
  const [selectedTab, setSelectedTab]   = useState("tab1");
  const user                            = useSelector(selectUser);
  const token                           = localStorage.getItem(TOKEN_KEY);
  const dispatch                        = useDispatch<AppDispatch>();

  const [products, setProducts]             = useState<Product[]>([]);
  const [allPlans, setAllPlans]             = useState<Plan[]>([]);
  const [priceMap, setPriceMap]             = useState<Record<string, Price>>({});
  const [selectedProduct, setSelectedProd]  = useState<Product | null>(null);
  const [loading, setLoading]               = useState(false);
  const [cycle, setCycle]                   = useState<BillingCycle>("monthly");
  const [pricingProduct, setPricingProduct] = useState<Product | null>(null);

  // ── Subscription flow state ────────────────────────────────
  const [subscriptionTarget, setSubscriptionTarget] = useState<{
    plan: Plan;
    price: Price;
    product: Product;
  } | null>(null);

  // ── Scroll refs ────────────────────────────────────────────
  const productScrollRef = useRef<HTMLDivElement>(null);
  const planScrollRef    = useRef<HTMLDivElement>(null);

  const scroll = (ref: React.RefObject<HTMLDivElement>, dir: "left" | "right") => {
    if (ref.current) ref.current.scrollBy({ left: dir === "right" ? 220 : -220, behavior: "smooth" });
  };

  // ── Subscribe click — opens SubscriptionFlow modal ─────────
  const handleSubscribeClick = (plan: Plan) => {
    const price   = priceMap[plan.id];
    const product = products.find(p => p.id === plan.productId);
    if (product && price) {
      setSubscriptionTarget({ plan, price, product });
    } else {
      console.warn("handleSubscribeClick: missing product or price", { planId: plan.id, productId: plan.productId, hasPrice: !!price, hasProduct: !!product });
    }
  };

  // ── Fetch products, plans, prices ─────────────────────────
  useEffect(() => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      fetch(`${BASE_URL}/Products/allProducts`, { headers: { Authorization: `Bearer ${token}`, accept: "*/*" } }).then(r => r.json()),
      fetch(`${BASE_URL}/Plans/public`,         { headers: { Authorization: `Bearer ${token}`, accept: "*/*" } }).then(r => r.json()),
      fetch(`${BASE_URL}/Prices`,               { headers: { Authorization: `Bearer ${token}`, accept: "*/*" } }).then(r => r.json()),
    ])
      .then(([prodJson, planJson, priceJson]) => {
        const prods: Product[] = prodJson?.data?.$values ?? [];
        setProducts(prods.filter(p => p.is_active));

        const plans: Plan[] = planJson?.data?.$values ?? [];
        setAllPlans(plans.filter(p => p.isActive && p.isPublic));

        const prices: Price[] = priceJson?.data?.$values ?? [];
        const map: Record<string, Price> = {};
        prices.forEach(price => {
          if (!map[price.plan_id] || price.currency === "USD") map[price.plan_id] = price;
        });
        setPriceMap(map);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  // ── Plans for selected product ─────────────────────────────
  const plansForProduct = useMemo(() => {
    if (!selectedProduct) return [];
    return allPlans
      .filter(p => p.productId === selectedProduct.id)
      .filter(p => {
        if (cycle === "lifetime") return p.billingInterval === "lifetime";
        if (cycle === "yearly")   return p.billingInterval === "yearly" || p.billingInterval === "monthly";
        return p.billingInterval === "monthly";
      })
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [allPlans, selectedProduct, cycle]);

  // ── Available intervals ────────────────────────────────────
  const availableIntervals = useMemo((): BillingCycle[] => {
    if (!selectedProduct) return ["monthly"];
    const intervals = new Set(allPlans.filter(p => p.productId === selectedProduct.id).map(p => p.billingInterval as BillingCycle));
    return (["monthly", "yearly", "lifetime"] as BillingCycle[]).filter(i => intervals.has(i));
  }, [allPlans, selectedProduct]);

  const handleProductClick = (product: Product) => {
    setSelectedProd(product);
    setPricingProduct(null);
    setSelectedTab("tab1");
    const intervals = new Set(allPlans.filter(p => p.productId === product.id).map(p => p.billingInterval));
    if (!intervals.has("monthly") && intervals.has("yearly")) setCycle("yearly");
    else setCycle("monthly");
  };

  // ── From Apps tab — goes straight to PricingPage ───────────
  const handleAppClick = (product: Product) => {
    setPricingProduct(product);
    setSelectedProd(null);
    setSelectedTab("tab1");
  };

  const handleTabClick = (value: string) => {
    setSelectedTab(value);
    if (value !== "tab1") setSelectedProd(null);
  };

  const styles = useStyles();
  const meta = selectedProduct ? (PRODUCT_META[selectedProduct.name] ?? DEFAULT_META) : DEFAULT_META;

  return (
    <>
      {/* ── Subscription Flow Modal ──────────────────────────── */}
      {subscriptionTarget && (
        <SubscriptionFlow
          product={subscriptionTarget.product}
          plan={subscriptionTarget.plan}
          price={subscriptionTarget.price}
          allPlans={allPlans.filter(p => p.productId === subscriptionTarget.product.id)}
          priceMap={priceMap}
          onClose={() => setSubscriptionTarget(null)}
          onSuccess={(plan: Plan, price: Price, email: string) => {
            console.log("✅ Subscribed:", plan.name, formatAmount(price.amount), email);
            setSubscriptionTarget(null);
          }}
        />
      )}

      <div className="CIPN-APP">
        <div className="react-spectrum-provider spectrum spectrum--light spectrum--medium">
          <div className="root-wrapper">

            {/* ── HEADER ────────────────────────────────────── */}
            <div className="fw-header">
              <div className="header-container">
                <div className="logo-container">
                  <a href="/" className="cilpron-logo disable-pointer cilpron-flex">
                    <img src={cilpron_logo} alt="CILPRON, Inc." />
                  </a>
                  <span className="CILPRON-LETTER-TEXT">C I L P R O N</span>
                </div>

                <div className="fw-parent-search-marketing-nav">
                  <div className="fw-search">
                    <div className="searchRegularAh">
                      <div className="nav-main">
                        <div className="action-item">
                          <div className="action-regular-takeover">
                            <div className="search-bar-no-takeover">
                              <div>
                                <button id="search-exit-button" className="spectrum-Button spectrum-Button--quiet spectrum-Button--primary search-exit-button-hidden" />
                                <div className={`search-bar-container adobeHome leftNavOn ${isActive ? "dropdownOn" : ""}`}>
                                  <div className="spectrum-Search">
                                    <input
                                      type="search"
                                      className="spectrum-Textfield spectrum-Search-input"
                                      placeholder="Search Cilpron"
                                      role="search"
                                      value=""
                                      onFocus={() => setIsActive(true)}
                                      onBlur={() => setIsActive(false)}
                                    />
                                    <SearchRegular className="spectrum-UIIcon-Magnifier spectrum-Icon spectrum-Icon--sizeS spectrum-Search-icon" />
                                  </div>
                                  {isActive && (
                                    <div className="light-mode">
                                      <div className="search-dropdown-container">
                                        <div className="search-dropdown-list">
                                          <div className="search-dropdown-recommendation">
                                            <div className="search-dropdown-recommendation-group">
                                              <h5 className="search-dropdown-recommendation-group-title">Apps &amp; services</h5>
                                              {products.slice(0, 4).map((p) => {
                                                const m = PRODUCT_META[p.name] ?? DEFAULT_META;
                                                return (
                                                  <div key={p.id} className="search-dropdown-recommendation-item" role="button"
                                                    onClick={() => { handleProductClick(p); setIsActive(false); }}>
                                                    <div className="search-dropdown-recommendation-item-text-container">
                                                      <div className="search-dropdown-recommendation-item-icon-container">
                                                        <div style={{ width: 28, height: 28, borderRadius: 4, background: m.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
                                                          {m.icon}
                                                        </div>
                                                      </div>
                                                      <div className="search-dropdown-recommendation-item-text-title-container">
                                                        <div className="search-dropdown-recommendation-item-text-title"><span>{p.name}</span></div>
                                                        <div className="search-dropdown-recommendation-item-text-type"><span>{p.description}</span></div>
                                                      </div>
                                                      <span className="search-dropdown-recommendation-item-text-launch-button">View plans</span>
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                              <hr className="horizontal-divider" />
                                              <div className="search-dropdown-quick-links">
                                                <div className="search-dropdown-quick-links-group-container">
                                                  <div className="search-dropdown-quick-links-group">
                                                    {["Help", "Contact us", "Cilpron Account"].map((label, i, arr) => (
                                                      <div key={label} className="search-dropdown-quick-links-item">
                                                        <div className="search-dropdown-quick-links-item-link"><span>{label}</span></div>
                                                        {i < arr.length - 1 && <span>&nbsp;&nbsp;| </span>}
                                                      </div>
                                                    ))}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div id="ccs-searchbar-hidden-trigger" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="fw-marketingNav">
                    <div className="marketing-nav">
                      <MegaMenu
                        products={products}
                        onProductClick={handleProductClick}
                        onViewAll={() => { setSelectedProd(null); setSelectedTab("tab1"); }}
                      />
                    </div>
                  </div>
                </div>

                <div className="topright-nav-container">
                  <div className="universal-nav-container">
                    <div id="universal-nav" className="universal-nav-light">
                      <div className="universal-nav-divider" />
                      <div className="unav-comp-help unav-comp-icon"><ChatHelpRegular /></div>
                      <div className="unav-comp-external-notifications"><AlertBadgeRegular /></div>
                      <AppSwitcher products={products} onAppClick={(p) => { handleProductClick(p); }} />
                      <UserProfile user={user} onLogout={() => dispatch(logoutThunk())} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── SIDENAV ───────────────────────────────────── */}
            <div className="fw-sidenav">
              <div className="base-theme">
                <nav className="sidenav-menu">
                  <div className="ah-sidenav">
                    <TabList defaultSelectedValue="tab1" vertical>
                      {[
                        { value: "tab1", icon: <Home24Regular className="icon-sizes" />,      label: "Home" },
                        { value: "tab2", icon: <Diversity24Regular className="icon-sizes" />, label: "Apps" },
                        { value: "tab3", icon: <Payment24Regular className="icon-sizes" />,   label: "Billing" },
                        { value: "tab4", icon: <LearningAppFilled className="icon-sizes" />,  label: "Learn" },
                      ].map(({ value, icon, label }) => (
                        <Tab key={value} className="sn-item" value={value} onClick={() => handleTabClick(value)}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            {icon}
                            <span className="nav-text">{label}</span>
                          </div>
                        </Tab>
                      ))}
                    </TabList>
                  </div>
                </nav>
              </div>
            </div>

            {/* ── BODY ──────────────────────────────────────── */}
            <div className="fw-body">
              <div className="fw-paymentBanner" />
              <div className="fw-applet-container">

                {/* ── TAB 1: HOME ───────────────────────────── */}
                {selectedTab === "tab1" && (
                  <>
                    {pricingProduct ? (
                      <PricingPage
                        product={pricingProduct}
                        onBack={() => setPricingProduct(null)}
                        onSubscribe={(plan, price) => {
                          if (pricingProduct && price) {
                            setSubscriptionTarget({ plan, price, product: pricingProduct });
                          }
                        }}
                      />
                    ) : (
                      <section id="bill-board" className="hero-section">
                        <div className="hero-section-content">

                          {/* Left column */}
                          <div className="hero-user-section">
                            <div className="CPN-welcome-message">
                              <div className="profile-container">
                                <div className="welcome-message">
                                  Welcome to Cilpron,
                                  <span className="first-text">{user?.firstName}</span>
                                </div>
                                <div className="profile-section">
                                  <Persona
                                    name={`${user?.firstName ?? ""} ${user?.lastName ?? ""}`}
                                    secondaryText="1 Assets Purchased"
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="recommended-apps-container">
                              <div className="apps-section">
                                <h2 className="spectrum-Heading spectrum-Heading--subtitle1 apps-section-header">Your apps</h2>
                                <div className="your-apps-container">
                                  <div className="app-grid">
                                    <div className="trial-card purchased-app">
                                      <div className="trial-widget-header">
                                        <div className="trial-widget-title">All purchased apps</div>
                                        <a className="trial-card-view-more" href="#" aria-label="View all" />
                                      </div>
                                      <div className="trial-card-scroll-container">
                                        <div className="trial-subcards-section">
                                          <div className="app-details-card app-details-focus-card">
                                            <div className="app-details-card-content">
                                              <div className="app-details-card-header">
                                                <div className="app-details-card-title-section">
                                                  <div className="app-details-card-app-logo">
                                                    <img src={HMS} alt="hms" />
                                                  </div>
                                                  <div className="app-details-card-title">
                                                    <h3 className="header-text">Hospitality</h3>
                                                  </div>
                                                </div>
                                              </div>
                                              <div className="app-details-card-body" />
                                            </div>
                                            <div className="app-details-card-cta-container">
                                              <button className="spectrum-Button spectrum-Button--secondary app-details-card-cta-free-trial">
                                                <span className="spectrum-Button-label">Login</span>
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Right column */}
                          <div className="hero-widget-section">

                            {/* ── PLANS / PRODUCTS CARD ─────────── */}
                            <div className="trial-card">

                              {/* Header row */}
                              <div className="trial-widget-header">
                                {selectedProduct ? (
                                  <button
                                    onClick={() => setSelectedProd(null)}
                                    style={{ background: "none", border: "none", fontSize: 13, fontWeight: 600, color: "#1473e6", cursor: "pointer", padding: 0, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}
                                  >
                                    ← Products
                                  </button>
                                ) : (
                                  <div className="trial-widget-title">Browse popular plans</div>
                                )}

                                {selectedProduct ? (
                                  <div style={{ display: "flex", background: "#f5f5f5", borderRadius: 6, padding: 2, gap: 2 }}>
                                    {availableIntervals.map((c) => (
                                      <button
                                        key={c}
                                        onClick={() => setCycle(c)}
                                        style={{
                                          padding: "4px 12px", border: "none", borderRadius: 4,
                                          fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                                          background: cycle === c ? "#fff" : "transparent",
                                          color: cycle === c ? "#2c2c2c" : "#8e8e8e",
                                          boxShadow: cycle === c ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                                          transition: "all 0.15s ease", display: "flex", alignItems: "center", gap: 4,
                                        }}
                                      >
                                        {INTERVAL_LABELS[c]}
                                        {c === "yearly" && (
                                          <span style={{ fontSize: 9, background: "#d1fae5", color: "#065f46", padding: "1px 4px", borderRadius: 6, fontWeight: 700 }}>
                                            Save 20%
                                          </span>
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                ) : (
                                  <a
                                    className="trial-card-view-more"
                                    href="#"
                                    aria-label="View all"
                                    onClick={(e) => { e.preventDefault(); handleTabClick("tab2"); }}
                                  >Browse all</a>
                                )}
                              </div>

                              {/* Product hero bar */}
                              {selectedProduct && (
                                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: meta.bg, borderRadius: 8, marginBottom: 12 }}>
                                  <span style={{ fontSize: 24, flexShrink: 0 }}>{meta.icon}</span>
                                  <div style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: meta.color }}>{selectedProduct.name}</div>
                                    <div style={{ fontSize: 11, color: "#6e6e6e", lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {selectedProduct.description}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Loading */}
                              {loading && (
                                <div style={{ padding: "16px 0", display: "flex", alignItems: "center", gap: 8 }}>
                                  <div className="cpn-spinner" />
                                  <span style={{ fontSize: 13, color: "#8e8e8e" }}>Loading…</span>
                                </div>
                              )}

                              {/* PRODUCT LIST */}
                              {!loading && !selectedProduct && (
                                <div className="trial-card-scroll-container" style={{ position: "relative" }}>
                                  <button
                                    onClick={() => scroll(productScrollRef, "left")}
                                    aria-label="Scroll left"
                                    style={{
                                      position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)",
                                      zIndex: 2, width: 26, height: 26, borderRadius: "50%",
                                      border: "1.5px solid #e2e8f0", background: "#fff",
                                      boxShadow: "0 1px 4px rgba(0,0,0,0.10)", cursor: "pointer",
                                      display: "flex", alignItems: "center", justifyContent: "center",
                                      color: "#475569", padding: 0, transition: "background 0.15s",
                                    }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "#f1f5f9"; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "#fff"; }}
                                  >
                                    <ChevronLeftRegular fontSize={14} />
                                  </button>
                                  <button
                                    onClick={() => scroll(productScrollRef, "right")}
                                    aria-label="Scroll right"
                                    style={{
                                      position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)",
                                      zIndex: 2, width: 26, height: 26, borderRadius: "50%",
                                      border: "1.5px solid #e2e8f0", background: "#fff",
                                      boxShadow: "0 1px 4px rgba(0,0,0,0.10)", cursor: "pointer",
                                      display: "flex", alignItems: "center", justifyContent: "center",
                                      color: "#475569", padding: 0, transition: "background 0.15s",
                                    }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "#f1f5f9"; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "#fff"; }}
                                  >
                                    <ChevronRightRegular fontSize={14} />
                                  </button>
                                  <div ref={productScrollRef} style={{ display: "flex", flexDirection: "row", overflowX: "auto", gap: 10, paddingBottom: 8, paddingLeft: 32, paddingRight: 32, flexWrap: "nowrap", scrollbarWidth: "none" }}>
                                    {products.map((product) => {
                                      const m = PRODUCT_META[product.name] ?? DEFAULT_META;
                                      const planCount = allPlans.filter(p => p.productId === product.id).length;
                                      return (
                                        <div
                                          key={product.id}
                                          role="button"
                                          tabIndex={0}
                                          onClick={() => handleProductClick(product)}
                                          onKeyDown={(e) => e.key === "Enter" && handleProductClick(product)}
                                          style={{
                                            minWidth: 160, width: 200, flexShrink: 0,
                                            background: "#f8f8f8", borderRadius: 10, padding: 12,
                                            cursor: "pointer", border: "1.5px solid transparent",
                                            display: "flex", flexDirection: "column", gap: 8,
                                            boxSizing: "border-box", outline: "none",
                                            transition: "box-shadow 0.18s ease, transform 0.13s ease, border-color 0.18s ease",
                                          }}
                                          onMouseEnter={(e) => {
                                            const el = e.currentTarget as HTMLDivElement;
                                            el.style.boxShadow = "0 4px 14px rgba(0,0,0,0.12)";
                                            el.style.transform = "translateY(-2px)";
                                            el.style.borderColor = m.color + "44";
                                          }}
                                          onMouseLeave={(e) => {
                                            const el = e.currentTarget as HTMLDivElement;
                                            el.style.boxShadow = "none";
                                            el.style.transform = "translateY(0)";
                                            el.style.borderColor = "transparent";
                                          }}
                                          onMouseDown={(e) => { (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)"; }}
                                        >
                                          <div style={{ width: 36, height: 36, borderRadius: 8, background: m.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
                                            {m.icon}
                                          </div>
                                          <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: "#2c2c2c", marginBottom: 4, lineHeight: 1.2 }}>{product.name}</div>
                                            <div style={{ fontSize: 11, color: "#6e6e6e", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                              {product.description}
                                            </div>
                                          </div>
                                          {planCount > 0 && (
                                            <div style={{ fontSize: 10, color: m.color, fontWeight: 600 }}>
                                              {planCount} plan{planCount !== 1 ? "s" : ""} →
                                            </div>
                                          )}
                                          <div style={{ display: "flex", gap: 6 }}>
                                            <button
                                              className="spectrum-Button spectrum-Button--secondary app-details-card-cta-free-trial"
                                              style={{ flex: 1, minWidth: 0 }}
                                              onClick={(e) => { e.stopPropagation(); handleProductClick(product); }}
                                            >
                                              <span className="spectrum-Button-label">Try</span>
                                            </button>
                                            <button
                                              className="spectrum-Button spectrum-Button--cta app-details-card-cta-upgrade"
                                              style={{ flex: 1, minWidth: 0 }}
                                              onClick={(e) => { e.stopPropagation(); handleProductClick(product); }}
                                            >
                                              <span className="spectrum-Button-label">Buy</span>
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* PLAN LIST — no plans */}
                              {!loading && selectedProduct && plansForProduct.length === 0 && (
                                <div style={{ padding: "24px 0", textAlign: "center" }}>
                                  <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
                                  <p style={{ fontSize: 13, color: "#4b4b4b", margin: "0 0 4px", fontWeight: 600 }}>No {cycle} plans yet</p>
                                  <p style={{ fontSize: 12, color: "#8e8e8e", margin: 0 }}>Try switching to a different billing period above.</p>
                                </div>
                              )}

                              {/* PLAN LIST — with plans */}
                              {!loading && selectedProduct && plansForProduct.length > 0 && (
                                <div className="trial-card-scroll-container" style={{ position: "relative" }}>
                                  <button
                                    onClick={() => scroll(planScrollRef, "left")}
                                    aria-label="Scroll left"
                                    style={{
                                      position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)",
                                      zIndex: 2, width: 26, height: 26, borderRadius: "50%",
                                      border: "1.5px solid #e2e8f0", background: "#fff",
                                      boxShadow: "0 1px 4px rgba(0,0,0,0.10)", cursor: "pointer",
                                      display: "flex", alignItems: "center", justifyContent: "center",
                                      color: "#475569", padding: 0, transition: "background 0.15s",
                                    }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "#f1f5f9"; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "#fff"; }}
                                  >
                                    <ChevronLeftRegular fontSize={14} />
                                  </button>
                                  <button
                                    onClick={() => scroll(planScrollRef, "right")}
                                    aria-label="Scroll right"
                                    style={{
                                      position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)",
                                      zIndex: 2, width: 26, height: 26, borderRadius: "50%",
                                      border: "1.5px solid #e2e8f0", background: "#fff",
                                      boxShadow: "0 1px 4px rgba(0,0,0,0.10)", cursor: "pointer",
                                      display: "flex", alignItems: "center", justifyContent: "center",
                                      color: "#475569", padding: 0, transition: "background 0.15s",
                                    }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "#f1f5f9"; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "#fff"; }}
                                  >
                                    <ChevronRightRegular fontSize={14} />
                                  </button>
                                  <div ref={planScrollRef} style={{ display: "flex", flexDirection: "row", overflowX: "auto", gap: 10, paddingBottom: 8, paddingLeft: 32, paddingRight: 32, flexWrap: "nowrap", scrollbarWidth: "none" }}>
                                    {plansForProduct.map((plan, i) => {
                                      const price         = priceMap[plan.id];
                                      const { main, sub } = getPriceLabel(plan, price, cycle);
                                      const free          = isPlanFree(price);
                                      const isEnterprise  = plan.name.toLowerCase().includes("enterprise");
                                      const isLifetime    = plan.billingInterval === "lifetime";
                                      const isPopular     = i === 1 && plansForProduct.length > 2;
                                      const features      = plan.features?.$values?.filter(f => f.displayLabel) ?? [];

                                      return (
                                        <div
                                          key={plan.id}
                                          style={{
                                            minWidth: 175, width: 200, flexShrink: 0,
                                            background: isPopular ? meta.bg : "#f8f8f8",
                                            borderRadius: 10, padding: 12,
                                            border: isPopular ? `1.5px solid ${meta.color}` : "1.5px solid transparent",
                                            display: "flex", flexDirection: "column", gap: 6,
                                            position: "relative", boxSizing: "border-box",
                                          }}
                                        >
                                          {isPopular && (
                                            <div style={{
                                              position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)",
                                              background: meta.color, color: "#fff", fontSize: 9, fontWeight: 700,
                                              padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap",
                                              textTransform: "uppercase", letterSpacing: "0.4px",
                                            }}>Popular</div>
                                          )}
                                          {isLifetime && !isPopular && (
                                            <div style={{
                                              position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)",
                                              background: "#5B21B6", color: "#fff", fontSize: 9, fontWeight: 700,
                                              padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap",
                                              textTransform: "uppercase", letterSpacing: "0.4px",
                                            }}>⭐ Lifetime</div>
                                          )}

                                          <div style={{ fontSize: 13, fontWeight: 700, color: "#2c2c2c", lineHeight: 1.2 }}>
                                            {plan.name}
                                          </div>

                                          <div>
                                            <div style={{ fontSize: 22, fontWeight: 800, color: isEnterprise ? "#8e8e8e" : meta.color, lineHeight: 1, letterSpacing: "-0.5px" }}>
                                              {main}
                                            </div>
                                            <div style={{ fontSize: 10, color: "#8e8e8e", marginTop: 2 }}>{sub}</div>
                                            {price && price.pricing_model !== "flat" && (
                                              <div style={{ fontSize: 9, fontWeight: 700, color: "#7c3aed", background: "#ede9fe", padding: "1px 5px", borderRadius: 4, marginTop: 3, display: "inline-block", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                                                {price.pricing_model.replace("_", " ")}
                                              </div>
                                            )}
                                          </div>

                                          {plan.trialDays > 0 && (
                                            <div style={{ fontSize: 10, color: "#065f46", background: "#d1fae5", padding: "2px 6px", borderRadius: 4, width: "fit-content", fontWeight: 700 }}>
                                              {plan.trialDays}-day trial
                                            </div>
                                          )}

                                          {plan.description && (
                                            <div style={{ fontSize: 11, color: "#6e6e6e", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                              {plan.description}
                                            </div>
                                          )}

                                          {features.length > 0 && (
                                            <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 2 }}>
                                              {features.slice(0, 3).map((f) => (
                                                <div key={f.featureId} style={{ fontSize: 10, color: "#4b4b4b", display: "flex", alignItems: "center", gap: 4 }}>
                                                  <span style={{ color: meta.color, fontWeight: 700 }}>✓</span>
                                                  {f.displayLabel}
                                                </div>
                                              ))}
                                            </div>
                                          )}

                                          {/* CTA */}
                                          <div style={{ display: "flex", gap: 5, marginTop: "auto", paddingTop: 8 }}>
                                            {isEnterprise ? (
                                              <button className="spectrum-Button spectrum-Button--secondary app-details-card-cta-free-trial" style={{ flex: 1, minWidth: 0 }}>
                                                <span className="spectrum-Button-label" style={{ fontSize: 12, whiteSpace: "nowrap" }}>Contact sales</span>
                                              </button>
                                            ) : free ? (
                                              <button
                                                className="spectrum-Button spectrum-Button--secondary app-details-card-cta-free-trial"
                                                style={{ flex: 1, minWidth: 0 }}
                                                onClick={() => handleSubscribeClick(plan)}
                                              >
                                                <span className="spectrum-Button-label" style={{ fontSize: 12, whiteSpace: "nowrap" }}>Get started</span>
                                              </button>
                                            ) : (
                                              <>
                                                <button
                                                  className="spectrum-Button spectrum-Button--secondary app-details-card-cta-free-trial"
                                                  style={{ flex: 1, minWidth: 0 }}
                                                  onClick={() => handleSubscribeClick(plan)}
                                                >
                                                  <span className="spectrum-Button-label" style={{ fontSize: 12, whiteSpace: "nowrap" }}>Try</span>
                                                </button>
                                                <button
                                                  className="spectrum-Button spectrum-Button--cta app-details-card-cta-upgrade"
                                                  style={{ flex: 1, minWidth: 0, background: meta.color, borderColor: meta.color }}
                                                  onClick={() => handleSubscribeClick(plan)}
                                                >
                                                  <span className="spectrum-Button-label" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{isLifetime ? "Buy" : "Subscribe"}</span>
                                                </button>
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* File manager card */}
                            <div className="hero-card Cilpn-hoactive">
                              <div className="hero-card-header">
                                <div className="hero-card-subtitle-container">
                                  <div className="hero-card-eyebrow">Cilpron Productivity</div>
                                  <a className="hero-card-link-icon" href="#" aria-label="Quick actions">
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                                      <path d="m18,15.75v-3.71924c0-.41406-.33594-.75-.75-.75s-.75.33594-.75.75v3.71924c0,.41357-.33643.75-.75.75H4.25c-.41357,0-.75-.33643-.75-.75V4.25c0-.41357.33643-.75.75-.75h3.81104c.41406,0,.75-.33594.75-.75s-.33594-.75-.75-.75h-3.81104c-1.24072,0-2.25,1.00928-2.25,2.25v11.5c0,1.24072,1.00928,2.25,2.25,2.25h11.5c1.24072,0,2.25-1.00928,2.25-2.25Z"/>
                                      <path d="m19,1.75v4.24268c0,.41406-.33594.75-.75.75s-.75-.33594-.75-.75v-2.43213l-6.46973,6.46973c-.14648.14648-.33838.21973-.53027.21973s-.38379-.07324-.53027-.21973c-.29297-.29297-.29297-.76758,0-1.06055l6.46973-6.46973h-2.43213c-.41406,0-.75-.33594-.75-.75s.33594-.75.75-.75h4.24268c.41406,0,.75.33594.75.75Z"/>
                                    </svg>
                                  </a>
                                </div>
                                <div className="hero-card-title">Your File Manager</div>
                              </div>
                              <div className="hero-card-scroll-container">
                                <div className="hero-card-subcards-section">
                                  {[{ href: "#", label: "Manage your File" }, { href: "#", label: "Support App" }, { href: "#", label: "Email" }].map(({ href, label }) => (
                                    <div key={label} className="hero-subcard">
                                      <a href={href}>
                                        <div className="widget-image-link">
                                          <img className="widget-image" src="https://odin.adobe.com/adobe/dynamicmedia/deliver/dm-aid--fa328907-3aff-4a2d-a1dc-65f12c0fe5ee/QA_EditPDF.jpg" alt={label} />
                                          <div className="widget-image-overlay" />
                                        </div>
                                      </a>
                                      <a className="hero-subcard-description" href={href}>{label}</a>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </section>
                    )}
                  </>
                )}

                {/* ── TAB 2: APPS ───────────────────────────── */}
                {selectedTab === "tab2" && (
                  <section id="apps-container" className="apps-container">
                    <div className="cc-apps-container">
                      <div className="cc-apps-skeleton">
                        <div className="cch-vertical-split-view">
                          <div className="leftChild">
                            <div className="left-section">
                              <div className="left-section-header">
                                <div className="filter-container">
                                  <div className="categories-with-chevron">
                                    <div className="buttonGroup-section">
                                      <div className="spectrum-ButtonGroup">
                                        <div className="spectrum-ButtonGroup-item">
                                          <div className="buttonGroup-item">
                                            <div className="ccd-light-theme">
                                              <Button appearance="primary" icon={<AppsRegular />}>All apps</Button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="content-container padding-bottom-right-section-present">
                                <div className="horizontal-default-view">
                                  <div className="grid-view">
                                    <div className="grid-view-header"><span>Essential in your Businesses</span></div>
                                    <div className="grid-view-wrapper">
                                      <div className="section-cards-wrapper">
                                        {loading ? (
                                          <div style={{ padding: 24, display: "flex", alignItems: "center", gap: 8 }}>
                                            <div className="cpn-spinner" />
                                            <span style={{ fontSize: 13, color: "#8e8e8e" }}>Loading apps…</span>
                                          </div>
                                        ) : products.map((product) => {
                                          const m = PRODUCT_META[product.name] ?? DEFAULT_META;
                                          const planCount = allPlans.filter(p => p.productId === product.id).length;
                                          return (
                                            <div
                                              key={product.id}
                                              className="section-card appCard cpn-app-card"
                                              role="button"
                                              tabIndex={0}
                                              onClick={() => handleAppClick(product)}
                                              onKeyDown={(e) => e.key === "Enter" && handleAppClick(product)}
                                              style={{ cursor: "pointer" }}
                                            >
                                              <div className="alpha-card">
                                                <div className="card-info-section">
                                                  <div className="body clickable">
                                                    <div className="header">
                                                      <div className="left-section">
                                                        <div className="slice-media app-badge" style={{ background: m.bg, borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, width: 30, height: 29 }}>
                                                          {m.icon}
                                                        </div>
                                                        <span className="left-section-title">
                                                          <span className="title-text">{product.name}</span>
                                                        </span>
                                                      </div>
                                                    </div>
                                                    <div slot="subheading" className="subheading">
                                                      <h4 className="description truncate-description">{product.description}</h4>
                                                    </div>
                                                  </div>
                                                  <div className="footer">
                                                    <div className="left-section">
                                                      {planCount > 0 && (
                                                        <span style={{ fontSize: 10, color: m.color, fontWeight: 600 }}>
                                                          {planCount} plans
                                                        </span>
                                                      )}
                                                    </div>
                                                    <div className="right-section">
                                                      <Button shape="circular" size="small" className="neyveba common-button button max-width-single-button"
                                                        onClick={(e) => { e.stopPropagation(); handleAppClick(product); }}>
                                                        <span className="button-text common-button-text show-ellipse">View plans</span>
                                                      </Button>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                )}

                {selectedTab === "tab3" && <BillingInvoices />}
                {selectedTab === "tab4" && <div>Learn content goes here</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ── Small helper used in onSuccess log ─────────────────────────
function formatAmount(cents: number): string {
  const d = cents / 100;
  return `$${d % 1 === 0 ? d.toFixed(0) : d.toFixed(2)}`;
}

export default Dashboard;